import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useId } from 'react';
import HomePage from './components/HomePage';
import { GlobalErrorHandler } from './components/error/GlobalErrorHandler';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  LoadingStateManager,
  NotificationSystem,
  NetworkStatusManager,
  ScoreNotifProvider,
  notifyLearnerViewChange,
  useNotifications
} from './components/feedback';
import { ApiProvider } from './providers/ApiProvider';
import { AuthProvider } from './hooks/useAuth';
import { adminService } from './services/adminService';
import { authService } from './services/authService';
import { PersianOverlayLoader } from './components/shared/PersianLoader';
import Navigation from './components/shared/Navigation';
import FerdowsiBackground from './components/shared/FerdowsiBackground';
import PwaInstallBanner from './components/shared/PwaInstallBanner';
import PwaUpdateBanner from './components/shared/PwaUpdateBanner';
import PwaPushPermissionBanner from './components/shared/PwaPushPermissionBanner';
import { SimpleAdminOverlayLoader } from './components/shared/SimpleAdminLoader';

import { UserProfileDashboard } from './components/UserProfileDashboard';
import { usePersianLoader } from './hooks/usePersianLoader';
import usePerformanceOptimization from './hooks/usePerformanceOptimization';
import { useAppInit } from './hooks/useAppInit';
import { useTheme } from './hooks/useTheme';
import { ThemeModeToast } from './components/feedback/ThemeModeToast';
import { useSystemConfig } from './hooks/useSystemConfig';
import {
  consumeLearnerModalHistoryPop,
  LEARNER_MODAL_HISTORY_KEY,
  LEARNER_MODAL_NONCE_KEY,
} from './hooks/useHistoryBackClose';
import { BottomBanner } from './components/shared/BottomBanner';
import { LearnerSideNav } from './components/shared/LearnerSideNav';
import { normalizeLibraryBookId } from './utils/libraryBookDisplay';
import { LearnerSettingsModal } from './components/settings/LearnerSettingsModal';
import type { LearnerSettingsModalTabId } from './components/settings/LearnerSettingsModalTabs';
import { LEARNER_SETTINGS_MODAL_CLOSED_EVENT } from './constants/learnerPersianFonts';
import {
  OPEN_LEARNER_SETTINGS_EVENT,
  type OpenLearnerSettingsDetail,
} from './constants/learnerSettingsEvents';
import { useResponsive } from './components/shared/MobileResponsive';
import { VisibleBlockTrackerProvider, VisibleBlockTrackerResetter } from './components/learning/VisibleBlockTracker';
import { FontSizeProvider } from './components/learning/FontSizeContext';
import { StickyAudioProvider } from './contexts/StickyAudioContext';
import { LocaleProvider } from './providers/LocaleProvider';
import { LOCALE_EVENT_NAME } from './i18n';
import { useTranslation } from 'react-i18next';
import { LearnerEdgeActions } from './components/shared/LearnerEdgeActions';

// Import test utilities for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  import('./utils/apiLoggingTest').catch(console.warn);
  import('./utils/errorBoundaryTest').catch(console.warn);
}

import {
  LazyLogin,
  LazyRegister,
  LazyDashboard,
  LazyAdminLogin,
  LazyAdminRouter,
  LazyGrammar,
  LazyPronunciation,
  LazyCoursesSection,
  LazyLessonViewer,
  LazyBookViewer,
  LazyLibraryPage,
  LazyRouteWrapper,
  preloadCriticalRoutes,
  preloadAdminRoutes
} from './components/LazyRoutes';
import { QuizFlow } from './components/QuizFlow';
import { KnowledgePlacementWizard } from './components/learning/KnowledgePlacementWizard';
import { ExerciseFlow } from './components/ExerciseFlow';
import { TagContentView } from './components/TagContentView';
import type { PublicContentTagDto } from './services/contentTagService';
import { StaticPageView } from './components/StaticPageView';
import { getKeepLearningModalEntries, type LearningResumeEntry } from './utils/lastLearningPosition';
import { type KeepLearningChoiceModalHandle } from './components/shared/KeepLearningChoiceModal';
import { NonFarsiContentHintModal } from './components/learning/NonFarsiContentHintModal';
import { useNonFarsiContentGate } from './hooks/useNonFarsiContentGate';
import { contentService } from './services/contentService';
import { levelProgressionService } from './services/levelProgressionService';
import { emitKnowledgePlacementUpdated } from './utils/knowledgePlacementStorage';
import { ProductTourProvider } from './contexts/ProductTourContext';
import { resolveLearnerTourPageKey, tourPageKeyForDesign } from './learnerTour/learnerTourRegistry';
import { useLearnerHomeDesign } from './utils/learnerHomeDesign';
import { SIDE_NAV_DRAWER_TARGET_KEYS, useTourCollapsibleTargetGroup, useTourEnsureClosedTarget } from './learnerTour/tourTargetEvents';
type ViewType = 'home' | 'login' | 'register' | 'dashboard' | 'profile' | 'admin-login' | 'admin-dashboard' | 'grammar' | 'quiz' | 'knowledge-placement' | 'exercise-flow' | 'pronunciation' | 'dialogue' | 'courses' | 'tag-content' | 'lesson' | 'book' | 'library' | 'static-page';

/**
 * Learner SPA often leaves the address bar on /login or /register after client-side navigation.
 * Building history URLs from pathname then yields /login#profile (wrong tab, wrong DevTools label).
 * Use / for post-auth in-app navigation.
 */
function learnerPathForHistory(): string {
  const p = window.location.pathname;
  if (p === '/login' || p === '/register') return '/';
  return p || '/';
}

const LEARNER_PROFILE_TABS = ['profile', 'subscription', 'messages', 'favorites', 'comments', 'goals', 'progress'] as const;

function profileTabFromHash(): (typeof LEARNER_PROFILE_TABS)[number] | null {
  const hash = window.location.hash.replace('#', '');
  return (LEARNER_PROFILE_TABS as readonly string[]).includes(hash)
    ? (hash as (typeof LEARNER_PROFILE_TABS)[number])
    : null;
}

function isPushPreferenceOn(profile: { pushNotifications?: boolean; PushNotifications?: boolean } | null | undefined): boolean {
  if (!profile) return false;
  if (typeof profile.pushNotifications === 'boolean') return profile.pushNotifications;
  if (typeof profile.PushNotifications === 'boolean') return profile.PushNotifications;
  return false;
}

type StaticPageKey = 'About' | 'Contact' | 'Privacy' | 'Terms';

type LearnerHistoryState = {
  view?: string;
  bookId?: number;
  bookReturnView?: ViewType;
  /** Profile tab (comments, favorites, …) sitting under a lesson or book opened from My Profile. */
  profileTab?: string;
  /** Where knowledge placement should return when dismissed / browser Back. */
  placementReturnView?: ViewType;
  /** Nested courses navigation (catalog → course detail → lesson). */
  coursesInner?: 'catalog' | 'course' | 'lesson';
  courseId?: number;
  lessonId?: number;
  /** Public static page tab (About / Contact / Privacy / Terms). */
  staticPageKey?: StaticPageKey;
  /** Where the first public-page entry should return when the stack is exhausted. */
  staticPageReturnView?: ViewType;
};

type CoursesHistoryRestore = {
  epoch: number;
  inner: NonNullable<LearnerHistoryState['coursesInner']>;
  courseId?: number;
  lessonId?: number;
};

function snapshotLearnerHistory(): LearnerHistoryState {
  const prev = window.history.state;
  return typeof prev === 'object' && prev !== null ? { ...(prev as LearnerHistoryState) } : {};
}

const BOOK_RETURN_ORIGIN_VIEWS = new Set<ViewType>(['dashboard', 'library', 'profile', 'courses']);

/** Where BookViewer should return when the user goes back (browser or header). */
function resolveBookReturnView(fromView: ViewType, currentBookReturn: ViewType): ViewType {
  if (fromView === 'book') return currentBookReturn;
  if (BOOK_RETURN_ORIGIN_VIEWS.has(fromView)) return fromView;
  return 'dashboard';
}

/** Completes session after external OAuth redirects back with tokens in the URL hash (must render under NotificationSystem). */
function ExternalOAuthHandler({
  onLoginSuccess
}: {
  onLoginSuccess: (user: any) => Promise<void>;
}) {
  const { showError } = useNotifications();
  const { t } = useTranslation('auth');
  const onLoginSuccessRef = useRef(onLoginSuccess);
  const oauthHandledRef = useRef(false);
  onLoginSuccessRef.current = onLoginSuccess;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('oauth_error');
    if (!oauthErr) return;
    const map: Record<string, string> = {
      external_login_failed: t('oauth.signInCouldNotComplete'),
      google_remote_error: t('oauth.googleFailed'),
      microsoft_remote_error: t('oauth.microsoftFailed'),
      apple_remote_error: t('oauth.appleFailed'),
      yahoo_remote_error: t('oauth.yahooFailed'),
      email_required: t('oauth.emailRequired'),
      locked_out: t('oauth.lockedOut'),
      link_failed: t('oauth.linkFailed'),
      account_exists: t('oauth.accountExists'),
      create_failed: t('oauth.createFailed'),
      user_missing: t('oauth.userMissing'),
      server_error: t('oauth.serverError')
    };
    showError(map[oauthErr] || t('oauth.signInFailed'));
    const u = new URL(window.location.href);
    u.searchParams.delete('oauth_error');
    window.history.replaceState({}, '', u.pathname + u.search + u.hash);
  }, [showError, t]);

  useEffect(() => {
    const twoFactorOAuth = authService.parseOAuthTwoFactorFragment();
    if (twoFactorOAuth) {
      authService.clearOAuthFragment();
      // Register must not reuse an existing account's 2FA challenge (same email auto-link bug).
      if (twoFactorOAuth.oauth_context === 'register') {
        const registerUrl = `/register?oauth_error=${encodeURIComponent('account_exists')}`;
        window.location.replace(registerUrl);
        return;
      }
      const q = new URLSearchParams({
        two_factor: '1',
        token: twoFactorOAuth.twoFactorToken,
        setup: twoFactorOAuth.requiresTwoFactorSetup ? '1' : '0',
        method: twoFactorOAuth.twoFactorMethod
      });
      if (twoFactorOAuth.twoFactorEmailHint) {
        q.set('email_hint', twoFactorOAuth.twoFactorEmailHint);
      }
      const loginUrl = `/login?${q.toString()}`;
      if (window.location.pathname !== '/login') {
        window.location.replace(loginUrl);
      } else {
        // Already on sign-in: pass token via query so Login mounts after hash is cleared.
        window.history.replaceState({}, '', loginUrl);
      }
      return;
    }

    const parsed = authService.parseGoogleOAuthFragment();
    if (!parsed) return;
    if (oauthHandledRef.current) return;
    oauthHandledRef.current = true;
    void (async () => {
      try {
        authService.applyOAuthToken(parsed.token);
        const profile = await authService.getProfile(true);
        if (parsed.oauth_context === 'register' && parsed.isNewUser) {
          try {
            const systemSettings = await adminService.getSystemSettings();
            const defaultTrialDays = systemSettings.defaultTrialDays || 7;
            await adminService.initializeUserTrial(profile.id, defaultTrialDays);
          } catch (e) {
            console.warn('OAuth trial init:', e);
          }
        }
        authService.clearGoogleOAuthFragment();
        authService.syncAuthSession(profile);
        await onLoginSuccessRef.current(profile);
      } catch (e) {
        console.error('OAuth completion:', e);
        oauthHandledRef.current = false;
        showError(t('oauth.signInCouldNotComplete'));
      }
    })();
  }, [showError, t]);

  return null;
}

function App() {
  const { t } = useTranslation('common');
  const { loading, withLoading, stopLoading } = usePersianLoader();
  const { isMobile } = useResponsive();
  const {
    isOptimized,
    loading: performanceLoading,
    measureComponentRender
  } = usePerformanceOptimization({
    enableCaching: true,
    enableFontOptimization: true,
    enablePerformanceTracking: true
  });

  // Initialize app-wide services (poem cache, etc.)
  const { isInitializing } = useAppInit();

  // Load system security settings (session timeout) and wire inactivity tracker
  useSystemConfig();

  // Initialize theme (loads from localStorage and applies to document)
  useTheme();

  // Combine loading states with priority system
  const isAppLoading = loading.isLoading || performanceLoading || isInitializing;
  const loadingMessage = loading.isLoading && loading.message ? loading.message :
    isInitializing ? t('initializingPoems') :
    performanceLoading ? t('optimizingPerformance') :
      t('loading');

  const [dashboardKey, setDashboardKey] = useState(0);
  const [refreshDashboardTrigger, setRefreshDashboardTrigger] = useState(0);
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    // Check URL path for admin routes
    const path = window.location.pathname;
    if (path.startsWith('/admin')) {
      if (path === '/admin/login') {
        return 'admin-login';
      }
      // Check if admin is authenticated
      if (adminService.isAdminAuthenticated()) {
        return 'admin-dashboard';
      }
      return 'admin-login';
    }

    // Explicit login path (e.g. after session expired redirect)
    if (path === '/login') {
      return 'login';
    }
    if (path === '/register') {
      return 'register';
    }
    // Check if regular user is logged in
    const storedUser = localStorage.getItem('userProfile');
    const authToken = localStorage.getItem('authToken');
    if (storedUser && authToken) {
      return profileTabFromHash() ? 'profile' : 'dashboard';
    }
    return 'home';
  });
  const currentViewRef = useRef<ViewType>(currentView);

  const [user, setUser] = useState<any>(null);
  const [learnerSettingsOpen, setLearnerSettingsOpen] = useState(false);
  const [learnerSettingsInitialTab, setLearnerSettingsInitialTab] = useState<LearnerSettingsModalTabId | undefined>();
  const [learnerSettingsFocusInAppAlerts, setLearnerSettingsFocusInAppAlerts] = useState(false);
  const [learnerSettingsFocusRequestId, setLearnerSettingsFocusRequestId] = useState(0);
  const [bannerCollapsed, setBannerCollapsed] = useState(false);
  const [sideNavOpen, setSideNavOpen] = useState(false);

  useTourEnsureClosedTarget('side-nav-menu', sideNavOpen, setSideNavOpen);
  useTourCollapsibleTargetGroup(SIDE_NAV_DRAWER_TARGET_KEYS, sideNavOpen, setSideNavOpen);
  const sideNavPanelId = useId();

  useEffect(() => {
    setSideNavOpen(false);
  }, [currentView]);

  useEffect(() => {
    const onLocaleChange = () => setSideNavOpen(false);
    window.addEventListener(LOCALE_EVENT_NAME, onLocaleChange as EventListener);
    return () => window.removeEventListener(LOCALE_EVENT_NAME, onLocaleChange as EventListener);
  }, []);
  const [audioBarPinned, setAudioBarPinned] = useState(false);
  const [audioBarExtraChromePx, setAudioBarExtraChromePx] = useState(0);
  const [staticPageKey, setStaticPageKey] = useState<StaticPageKey>('About');
  const [, setStaticPageReturnView] = useState<ViewType>('dashboard');
  const staticPageReturnViewRef = useRef<ViewType>('dashboard');
  const [profileInitialTab, setProfileInitialTab] = useState<string | null>(() => profileTabFromHash());
  /** When set, Dashboard scrolls to that section (e.g. achievement notification → My achievements). */
  const [dashboardScrollToSectionId, setDashboardScrollToSectionId] = useState<string | null>(null);
  /** Flash highlight on a specific earned achievement badge after "Show achievement". */
  const [dashboardHighlightAchievementId, setDashboardHighlightAchievementId] = useState<number | null>(null);
  /** Full-screen admin loader during logout (API + SPA) — stays above dashboard until admin login is shown */
  const [adminLogoutOverlay, setAdminLogoutOverlay] = useState(false);
  // Set initial history state so browser back from profile restores correct view
  useEffect(() => {
    if (!window.location.pathname.startsWith('/admin')) {
      window.history.replaceState({ view: currentView }, '', window.location.pathname + window.location.search + window.location.hash);
    }
  }, []);

  // Fetch fresh user profile from DB on mount if authenticated
  useEffect(() => {
    const loadFreshUser = async () => {
      const hasStoredToken = !!localStorage.getItem('authToken');
      let canUseSession = authService.isAuthenticated();

      // If token expired during app downtime, try refresh first to keep user logged in.
      if (!canUseSession && hasStoredToken) {
        try {
          await authService.refreshToken();
          canUseSession = authService.isAuthenticated();
        } catch (error) {
          console.warn('App: Token refresh on startup failed, clearing session:', error);
          await authService.logout('token_refresh_failed').catch(() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('authTokenInfo');
            localStorage.removeItem('userProfile');
          });
          setCurrentView('home');
          return;
        }
      }

      if (canUseSession) {
        try {
          console.log('App: Fetching fresh user profile from DB...');
          const freshProfile = await authService.getProfile(true);
          console.log('App: Fresh profile loaded:', freshProfile);
          console.log('App: Fresh profile has photo:', freshProfile?.profilePhoto ? 'YES' : 'NO');
          setUser(freshProfile);
          if (isPushPreferenceOn(freshProfile)) {
            void import('./services/pushService').then(({ syncWebPushSubscriptionIfGranted }) => {
              void syncWebPushSubscriptionIfGranted();
            });
          }
        } catch (error) {
          console.error('App: Failed to load fresh profile:', error);
          // Fallback to localStorage if fetch fails
          const storedUser = localStorage.getItem('userProfile');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
            } catch (e) {
              console.error('App: Failed to parse stored user:', e);
            }
          }
        }
      }
    };

    loadFreshUser();
  }, []);

  useEffect(() => {
    const onPushPreference = (event: Event) => {
      const enabled = Boolean((event as CustomEvent<{ enabled?: boolean }>).detail?.enabled);
      setUser((prev: any) => (prev ? { ...prev, pushNotifications: enabled, PushNotifications: enabled } : prev));
    };
    window.addEventListener('app:pushPreferenceChanged', onPushPreference as EventListener);
    return () => window.removeEventListener('app:pushPreferenceChanged', onPushPreference as EventListener);
  }, []);

  useEffect(() => {
    const onOpenSettings = (event: Event) => {
      const detail = (event as CustomEvent<OpenLearnerSettingsDetail>).detail ?? {};
      if (detail.tab) setLearnerSettingsInitialTab(detail.tab);
      if (detail.focus === 'inAppAlerts') {
        setLearnerSettingsFocusInAppAlerts(true);
        setLearnerSettingsFocusRequestId((id) => id + 1);
      }
      setLearnerSettingsOpen(true);
    };
    window.addEventListener(OPEN_LEARNER_SETTINGS_EVENT, onOpenSettings as EventListener);
    return () => window.removeEventListener(OPEN_LEARNER_SETTINGS_EVENT, onOpenSettings as EventListener);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const type = event.data?.type;
      if (type === 'OPEN_INBOX') {
        window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view: 'profile', hash: 'messages' } }));
      }
      if (type === 'INBOX_PUSH' || type === 'OPEN_INBOX') {
        window.dispatchEvent(new CustomEvent('messageRead'));
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  /** First-login dashboard promo: hide permanently once learner starts learning elsewhere. */
  const consumeKnowledgePlacementDashboardIfNeeded = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    try {
      const state = await levelProgressionService.getKnowledgePlacementState();
      if (!state.shouldShowDashboardBanner) return;
      await levelProgressionService.dismissKnowledgePlacement();
      emitKnowledgePlacementUpdated();
    } catch (e) {
      console.warn('Knowledge placement consume (start learning) skipped:', e);
    }
  }, []);

  /** Profile tab to restore when Back is pressed on a lesson or book opened from My Profile. */
  const profileReturnTabRef = useRef<string | null>(null);

  const stampProfileReturnEntry = useCallback((tab: string) => {
    const url = `${learnerPathForHistory()}${window.location.search}#${tab}`;
    const prev = window.history.state;
    const base = typeof prev === 'object' && prev !== null ? { ...(prev as object) } : {};
    window.history.replaceState({ ...base, view: 'profile', profileTab: tab }, '', url);
    profileReturnTabRef.current = tab;
  }, []);

  const navigateToLessonAndConsumeKp = useCallback(
    (lessonId: number) => {
      profileReturnTabRef.current = null;
      void consumeKnowledgePlacementDashboardIfNeeded();
      setLessonScrollToBlockId(undefined);
      setLessonOpenTipId(undefined);
      setLessonOpenExerciseId(undefined);
      setLessonOpenQuizFromSearchId(undefined);
      setLessonHighlightTerm(undefined);
      setSelectedLessonId(lessonId);
      setCurrentView('lesson');
      window.history.pushState({ view: 'lesson' }, '', window.location.pathname || '/');
    },
    [consumeKnowledgePlacementDashboardIfNeeded]
  );

  const navigateToLessonFromProfile = useCallback(
    (lessonId: number) => {
      const tab = profileTabFromHash() ?? 'profile';
      stampProfileReturnEntry(tab);
      void consumeKnowledgePlacementDashboardIfNeeded();
      setLessonScrollToBlockId(undefined);
      setLessonOpenTipId(undefined);
      setLessonOpenExerciseId(undefined);
      setLessonOpenQuizFromSearchId(undefined);
      setLessonHighlightTerm(undefined);
      setSelectedLessonId(lessonId);
      setCurrentView('lesson');
      window.history.pushState(
        { view: 'lesson', profileTab: tab } satisfies LearnerHistoryState,
        '',
        learnerPathForHistory()
      );
    },
    [consumeKnowledgePlacementDashboardIfNeeded, stampProfileReturnEntry]
  );

  const [bookReturnView, setBookReturnView] = useState<ViewType>('dashboard');
  const bookReturnViewRef = useRef<ViewType>('dashboard');
  const [, setPlacementReturnView] = useState<ViewType>('dashboard');
  const placementReturnViewRef = useRef<ViewType>('dashboard');
  const [selectedBookId, setSelectedBookId] = useState<number | undefined>(undefined);
  const [bookInitialPageIndex, setBookInitialPageIndex] = useState<number | undefined>(undefined);
  const [bookScrollToBlockId, setBookScrollToBlockId] = useState<number | undefined>(undefined);
  const [bookHighlightTerm, setBookHighlightTerm] = useState<string | undefined>(undefined);

  const clearBookSearchHighlight = useCallback(() => {
    setBookScrollToBlockId(undefined);
    setBookHighlightTerm(undefined);
  }, []);

  const navigateToBook = useCallback((bookId: number, returnView: ViewType = 'dashboard') => {
    if (returnView !== 'profile') profileReturnTabRef.current = null;
    clearBookSearchHighlight();
    setBookReturnView(returnView);
    bookReturnViewRef.current = returnView;
    setSelectedBookId(bookId);
    setBookInitialPageIndex(undefined);
    setCurrentView('book');
    window.history.pushState(
      {
        view: 'book',
        bookId,
        bookReturnView: returnView,
        ...(returnView === 'profile' && profileReturnTabRef.current
          ? { profileTab: profileReturnTabRef.current }
          : {}),
      } satisfies LearnerHistoryState,
      '',
      learnerPathForHistory()
    );
  }, [clearBookSearchHighlight]);

  const handleBookViewerBack = useCallback(() => {
    if (bookReturnViewRef.current === 'profile' && profileReturnTabRef.current) {
      setProfileInitialTab(profileReturnTabRef.current);
    }
    window.history.back();
  }, []);

  const openStaticPage = useCallback((key: StaticPageKey) => {
    const from = currentViewRef.current;
    const returnView: ViewType =
      from === 'static-page' ? staticPageReturnViewRef.current : from;
    setStaticPageReturnView(returnView);
    staticPageReturnViewRef.current = returnView;
    setStaticPageKey(key);
    setCurrentView('static-page');
    window.history.pushState(
      {
        view: 'static-page',
        staticPageKey: key,
        staticPageReturnView: returnView,
      } satisfies LearnerHistoryState,
      '',
      learnerPathForHistory()
    );
  }, []);

  const navigateStaticPageTab = useCallback((key: StaticPageKey) => {
    setStaticPageKey(key);
    window.history.pushState(
      {
        view: 'static-page',
        staticPageKey: key,
        staticPageReturnView: staticPageReturnViewRef.current,
      } satisfies LearnerHistoryState,
      '',
      learnerPathForHistory()
    );
  }, []);

  const handleStaticPageBack = useCallback(() => {
    const state = window.history.state as LearnerHistoryState | null;
    if (state?.view === 'static-page') {
      window.history.back();
      return;
    }
    setCurrentView(staticPageReturnViewRef.current || 'dashboard');
  }, []);

  const openKnowledgePlacement = useCallback((fromView?: ViewType) => {
    const origin = fromView ?? currentViewRef.current;
    const returnView: ViewType =
      origin && origin !== 'knowledge-placement' ? origin : 'dashboard';
    setPlacementReturnView(returnView);
    placementReturnViewRef.current = returnView;
    setCurrentView('knowledge-placement');
    // Drop any leftover modal history markers before pushing placement so one Back returns cleanly.
    const prev = window.history.state;
    if (typeof prev === 'object' && prev !== null && LEARNER_MODAL_HISTORY_KEY in prev) {
      const cleaned = { ...(prev as Record<string, unknown>) };
      delete cleaned[LEARNER_MODAL_HISTORY_KEY];
      delete cleaned[LEARNER_MODAL_NONCE_KEY];
      window.history.replaceState(cleaned, '', learnerPathForHistory());
    }
    window.history.pushState(
      { view: 'knowledge-placement', placementReturnView: returnView } satisfies LearnerHistoryState,
      '',
      learnerPathForHistory()
    );
  }, []);

  const handleKnowledgePlacementBack = useCallback(() => {
    const ret = placementReturnViewRef.current || 'dashboard';
    setCurrentView(ret);
    // One-step return to the caller (profile/dashboard) without walking modal ghost entries.
    window.history.replaceState({ view: ret }, '', learnerPathForHistory());
  }, []);

  const handleKnowledgePlacementBackToDashboard = useCallback(() => {
    setCurrentView('dashboard');
    window.history.replaceState({ view: 'dashboard' }, '', learnerPathForHistory());
  }, []);

  const navigateToLibrary = useCallback(() => {
    setCurrentView('library');
    window.history.pushState({ view: 'library' }, '', learnerPathForHistory());
  }, []);

  const [lessonScrollToBlockId, setLessonScrollToBlockId] = useState<number | undefined>(undefined);
  const [lessonOpenTipId, setLessonOpenTipId] = useState<string | undefined>(undefined);
  const [lessonOpenExerciseId, setLessonOpenExerciseId] = useState<number | undefined>(undefined);
  const [lessonOpenQuizFromSearchId, setLessonOpenQuizFromSearchId] = useState<number | undefined>(
    undefined
  );
  /** From global search: substring for lesson block / modal highlights (LessonViewer). */
  const [lessonHighlightTerm, setLessonHighlightTerm] = useState<string | undefined>(undefined);
  const [coursesInitialCourseId, setCoursesInitialCourseId] = useState<number | undefined>(undefined);
  const [coursesInitialLevelId, setCoursesInitialLevelId] = useState<number | undefined>(undefined);
  const [coursesHistoryRestore, setCoursesHistoryRestore] = useState<CoursesHistoryRestore | null>(null);
  /** Catalog course-card flash (search hit on course title/description). */
  const [coursesSearchHighlightCourseId, setCoursesSearchHighlightCourseId] = useState<
    number | undefined
  >(undefined);
  const [coursesSearchHighlightTerm, setCoursesSearchHighlightTerm] = useState<string | undefined>(
    undefined
  );
  /** Course detail row flash (search hit on level / lesson title in structure). */
  const [coursesSearchDetailHighlightItem, setCoursesSearchDetailHighlightItem] = useState<
    { type: 'level' | 'lesson' | 'levelQuiz' | 'levelExercise'; id: number } | undefined
  >(undefined);
  const [coursesSearchDetailHighlightTerm, setCoursesSearchDetailHighlightTerm] = useState<
    string | undefined
  >(undefined);
  /** Quiz / exercise flow row flash when resuming from keep learning. */
  const [quizResumeHighlightItem, setQuizResumeHighlightItem] = useState<
    { flowKind: string; quizId: number } | undefined
  >(undefined);
  const [exerciseResumeHighlightItem, setExerciseResumeHighlightItem] = useState<
    { flowKind: string; exerciseId: number } | undefined
  >(undefined);
  const [learningSearchScope, setLearningSearchScope] = useState<{
    courseId?: number;
    levelId?: number;
    lessonId?: number;
    libraryBookId?: number;
    limitToLibrary?: boolean;
  }>({});

  const clearCoursesInitialCourse = useCallback(() => {
    setCoursesInitialCourseId(undefined);
  }, []);

  const clearCoursesInitialLevel = useCallback(() => {
    setCoursesInitialLevelId(undefined);
  }, []);

  const clearCoursesSearchCatalogHighlight = useCallback(() => {
    setCoursesSearchHighlightCourseId(undefined);
    setCoursesSearchHighlightTerm(undefined);
  }, []);

  const clearCoursesSearchDetailHighlight = useCallback(() => {
    setCoursesSearchDetailHighlightItem(undefined);
    setCoursesSearchDetailHighlightTerm(undefined);
  }, []);

  const clearQuizResumeHighlight = useCallback(() => {
    setQuizResumeHighlightItem(undefined);
  }, []);

  const clearExerciseResumeHighlight = useCallback(() => {
    setExerciseResumeHighlightItem(undefined);
  }, []);

  useEffect(() => {
    const handleLearnerSearchNavigate = (ev: Event) => {
      const e = ev as CustomEvent<{
        kind?: string;
        lessonId?: number;
        contentBlockId?: number;
        courseId?: number;
        levelId?: number;
        tipId?: string;
        lessonExerciseId?: number;
        lessonQuizId?: number;
        openQuizFromSearch?: boolean;
        highlightTerm?: string;
        highlightType?: string;
        highlightId?: number;
        libraryBookId?: number;
        libraryBookBlockId?: number;
      }>;
      const d = e.detail;
      if (!d?.kind) return;
      void consumeKnowledgePlacementDashboardIfNeeded();
      const term =
        typeof d.highlightTerm === 'string' && d.highlightTerm.trim().length > 0
          ? d.highlightTerm.trim()
          : undefined;
      const detailHighlightFromEvent = (): {
        type: 'level' | 'lesson' | 'levelQuiz' | 'levelExercise';
        id: number;
      } | undefined => {
        const id = d.highlightId;
        if (id == null || id <= 0) return undefined;
        const t = d.highlightType;
        if (t === 'level' || t === 'lesson' || t === 'levelQuiz' || t === 'levelExercise') {
          return { type: t, id };
        }
        return undefined;
      };

      if (d.kind === 'book' && d.libraryBookId != null && d.libraryBookId > 0) {
        clearCoursesSearchCatalogHighlight();
        clearCoursesSearchDetailHighlight();
        setLessonHighlightTerm(undefined);
        const returnView = resolveBookReturnView(
          currentViewRef.current,
          bookReturnViewRef.current
        );
        navigateToBook(d.libraryBookId, returnView);
        setBookScrollToBlockId(
          d.libraryBookBlockId != null && d.libraryBookBlockId > 0 ? d.libraryBookBlockId : undefined
        );
        setBookHighlightTerm(term);
        return;
      }

      if (d.kind === 'lesson' && d.lessonId != null && d.lessonId > 0) {
        clearCoursesSearchCatalogHighlight();
        clearCoursesSearchDetailHighlight();
        const openExercise = d.lessonExerciseId != null && d.lessonExerciseId > 0;
        const openQuizSearch =
          d.openQuizFromSearch === true && d.lessonQuizId != null && d.lessonQuizId > 0;
        setLessonScrollToBlockId(
          openExercise || openQuizSearch
            ? undefined
            : d.contentBlockId != null && d.contentBlockId > 0
              ? d.contentBlockId
              : undefined
        );
        setLessonOpenTipId(
          openExercise || openQuizSearch
            ? undefined
            : typeof d.tipId === 'string' && d.tipId.length > 0
              ? d.tipId
              : undefined
        );
        setLessonOpenExerciseId(openExercise ? d.lessonExerciseId : undefined);
        setLessonOpenQuizFromSearchId(openQuizSearch ? d.lessonQuizId : undefined);
        setLessonHighlightTerm(term);
        setSelectedLessonId(d.lessonId);
        setCurrentView('lesson');
        window.history.pushState({ view: 'lesson' }, '', learnerPathForHistory());
        return;
      }
      if (d.kind === 'level' && d.courseId != null && d.courseId > 0 && d.levelId != null && d.levelId > 0) {
        clearCoursesSearchCatalogHighlight();
        setLessonHighlightTerm(undefined);
        setCoursesInitialCourseId(d.courseId);
        setCoursesInitialLevelId(d.levelId);
        const hi = detailHighlightFromEvent();
        setCoursesSearchDetailHighlightItem(hi);
        setCoursesSearchDetailHighlightTerm(term);
        setCurrentView('courses');
        window.history.pushState({ view: 'courses', coursesInner: 'catalog' }, '', learnerPathForHistory());
        return;
      }
      if (d.kind === 'course' && d.courseId != null && d.courseId > 0) {
        setLessonHighlightTerm(undefined);
        clearCoursesSearchDetailHighlight();
        setCoursesInitialCourseId(undefined);
        setCoursesInitialLevelId(undefined);
        setCoursesSearchHighlightCourseId(d.courseId);
        setCoursesSearchHighlightTerm(term);
        setCurrentView('courses');
        setCoursesHistoryRestore({ epoch: Date.now(), inner: 'catalog' });
        window.history.pushState({ view: 'courses', coursesInner: 'catalog' }, '', learnerPathForHistory());
      }
    };

    window.addEventListener('app:learnerSearchNavigate', handleLearnerSearchNavigate as EventListener);
    return () => {
      window.removeEventListener('app:learnerSearchNavigate', handleLearnerSearchNavigate as EventListener);
    };
  }, [
    consumeKnowledgePlacementDashboardIfNeeded,
    clearCoursesSearchCatalogHighlight,
    clearCoursesSearchDetailHighlight,
    navigateToBook
  ]);

  useEffect(() => {
    if (currentView === 'book' && selectedBookId != null && selectedBookId > 0) {
      setLearningSearchScope({ libraryBookId: selectedBookId });
      return;
    }
    if (currentView === 'library') {
      setLearningSearchScope({ limitToLibrary: true });
      return;
    }
    if (currentView !== 'courses' && currentView !== 'lesson') {
      setLearningSearchScope({});
    }
  }, [currentView, selectedBookId]);

  useEffect(() => {
    if (currentView.startsWith('admin')) return;
    notifyLearnerViewChange(currentView);
  }, [currentView]);

  const canLimitSearchToLearningContext = useMemo(() => {
    if (currentView === 'lesson') return true;
    if (currentView === 'library' || currentView === 'book') return true;
    if (currentView !== 'courses') return false;
    return (
      learningSearchScope.courseId != null ||
      learningSearchScope.levelId != null ||
      learningSearchScope.lessonId != null
    );
  }, [currentView, learningSearchScope]);

  const [homeDesign] = useLearnerHomeDesign();

  useEffect(() => {
    const root = document.documentElement;
    if (!user || currentView.startsWith('admin')) {
      delete root.dataset.learnerDesign;
      return;
    }
    root.dataset.learnerDesign = homeDesign;
  }, [user, currentView, homeDesign]);

  const learnerTourPageKey = useMemo(() => {
    if (!user || currentView.startsWith('admin')) return null;
    const resolved = resolveLearnerTourPageKey(currentView);
    if (currentView === 'dashboard') return tourPageKeyForDesign('dashboard', homeDesign);
    return resolved;
  }, [user, currentView, homeDesign]);

  // Listen for custom navigation events (e.g., from locked lesson / exercise modal)
  useEffect(() => {
    const handleAppNavigate = (event: CustomEvent) => {
      const { view, hash } = event.detail || {};
      if (view === 'profile') {
        // Already on My Profile: switch tab only — must run before auth check.
        // Otherwise a transient false from isAuthenticated() (token timing / storage) can send the user to login while still on this screen (e.g. Favorites → Profile from bottom banner).
        if (currentViewRef.current === 'profile' && typeof hash === 'string' && hash.length > 0) {
          setProfileInitialTab(hash === 'menus' ? null : hash);
          const base = learnerPathForHistory();
          const tabUrl = base + window.location.search + `#${hash}`;
          const currentHash = window.location.hash.replace('#', '');
          if (currentHash === hash) {
            if (hash === 'menus') window.dispatchEvent(new HashChangeEvent('hashchange'));
            return;
          }
          window.history.pushState({ view: 'profile' }, '', tabUrl);
          if (hash === 'menus') window.dispatchEvent(new HashChangeEvent('hashchange'));
          return;
        }
        if (!authService.isAuthenticated()) {
          setCurrentView('login');
          return;
        }
        // Ensure current history entry has the correct view so back from profile returns here (e.g. lesson/courses/dashboard)
        const fromView = currentViewRef.current;
        const basePath = learnerPathForHistory();
        const url = basePath + window.location.search + (hash ? `#${hash}` : '');
        window.history.replaceState(
          { ...snapshotLearnerHistory(), view: fromView },
          '',
          basePath + window.location.search + (window.location.hash || '')
        );
        setProfileInitialTab(hash === 'menus' ? null : hash || null);
        setCurrentView('profile');
        // Keep ref in sync immediately; useEffect runs after paint and was leaving ref stale as 'dashboard'
        // while currentView was already 'profile', so banner tab switches missed the early return and hit !auth → login.
        currentViewRef.current = 'profile';
        window.history.pushState({ view: 'profile' }, '', url);
        if (hash === 'menus') window.dispatchEvent(new HashChangeEvent('hashchange'));
        return;
      }

      if (view === 'quiz' || view === 'exercise-flow') {
        if (!authService.isAuthenticated()) {
          setCurrentView('login');
          return;
        }
        const detail = event.detail || {};
        if (view === 'quiz') {
          const quizId = Number(detail.quizId);
          const flowKind = typeof detail.flowKind === 'string' ? detail.flowKind : '';
          setExerciseResumeHighlightItem(undefined);
          setQuizResumeHighlightItem(quizId > 0 && flowKind ? { flowKind, quizId } : undefined);
          setCurrentView('quiz');
          window.history.pushState({ view: 'quiz' }, '', window.location.pathname || '/');
          return;
        }
        const exerciseId = Number(detail.exerciseId);
        const flowKind = typeof detail.flowKind === 'string' ? detail.flowKind : '';
        setQuizResumeHighlightItem(undefined);
        setExerciseResumeHighlightItem(
          exerciseId > 0 && flowKind ? { flowKind, exerciseId } : undefined
        );
        setCurrentView('exercise-flow');
        window.history.pushState({ view: 'exercise-flow' }, '', window.location.pathname || '/');
      }
    };

    window.addEventListener('app:navigate', handleAppNavigate as EventListener);
    return () => {
      window.removeEventListener('app:navigate', handleAppNavigate as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleAudioBarState = (event: Event) => {
      const customEvent = event as CustomEvent<{
        visible?: boolean;
        pinned?: boolean;
        extraChromeBottomPx?: number;
      }>;
      const visible = customEvent.detail?.visible === true;
      const pinned = visible && customEvent.detail?.pinned === true;
      setAudioBarPinned(pinned);
      setAudioBarExtraChromePx(
        pinned ? (customEvent.detail?.extraChromeBottomPx ?? 0) : 0
      );
    };

    window.addEventListener('app:audioBarState', handleAudioBarState as EventListener);
    return () => {
      window.removeEventListener('app:audioBarState', handleAudioBarState as EventListener);
    };
  }, []);

  // Add timeout to prevent app from getting stuck in loading state
  // But only for initial load, not for session expiration scenarios
  useEffect(() => {
    if (isAppLoading && !user && !authService.isAuthenticated()) {
      // Only apply timeout for initial app loading when user is not authenticated
      // This prevents interfering with session expiration handling
      const timeoutId = setTimeout(() => {
        console.warn('App: Initial loading state exceeded timeout, forcing stop');
        // Force stop loading after 30 seconds only if still loading and not authenticated
        if (loading.isLoading && !user && !authService.isAuthenticated()) {
          stopLoading();
        }
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isAppLoading, loading.isLoading, user, stopLoading]);

  // Handle visibility change to check token validity (but respect session expiration settings)
  // Note: We don't force refresh here to respect system configuration session/token expiration
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && user && authService.isAuthenticated()) {
        // User returned to the tab - only check if token is still valid
        // Don't force refresh as session expiration is configured in system settings
        try {
          // Just verify authentication is still valid - don't force refresh
          // The existing token refresh mechanism will handle expiration based on system config
          const isAuthenticated = authService.isAuthenticated();
          if (!isAuthenticated) {
            // Token expired - let the normal session expiration flow handle it
            console.log('App: Token expired, session will be handled by system configuration');
          }
        } catch (error) {
          // Silently handle - don't interfere with session expiration logic
          console.warn('App: Error checking authentication on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const handleLoginSuccess = withLoading(async (userData: any) => {
    console.log('App: handleLoginSuccess called with userData:', userData);
    
    // Validate that we have valid user data before proceeding
    if (!userData || !userData.id || !userData.email) {
      console.error('App: handleLoginSuccess called with invalid user data:', userData);
      return;
    }
    
    // Set initial user data from login response
    setUser(userData);
    
    // Fetch fresh profile from DB to ensure we have latest photo
    try {
      console.log('App: Fetching fresh profile from DB after login...');
      const freshProfile = await authService.getProfile(true);
      console.log('App: Fresh profile loaded after login:', freshProfile);
      console.log('App: Fresh profile has photo:', freshProfile?.profilePhoto ? 'YES' : 'NO');
      setUser(freshProfile);
      authService.syncAuthSession(freshProfile);
      if (isPushPreferenceOn(freshProfile)) {
        void import('./services/pushService').then(({ syncWebPushSubscriptionIfGranted }) => {
          void syncWebPushSubscriptionIfGranted();
        });
      }
    } catch (error) {
      console.error('App: Failed to load fresh profile after login:', error);
      // Keep the userData from login response if fetch fails
    }
    
    // Consistent delay for smooth transition
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Final validation before redirect
    if (!userData || !userData.id || !userData.email) {
      console.error('App: Final validation failed - NOT redirecting to dashboard');
      console.error('App: Invalid user data at redirect time:', userData);
      return;
    }
    
    console.log('App: Setting current view to dashboard');
    setCurrentView('dashboard');
    window.history.pushState({ view: 'dashboard' }, '', '/' + window.location.search);
  }, t('loaders.loadingDashboard'));

  const handleLogout = async () => {
    // Use authService.logout() to ensure proper logging and cleanup
    await authService.logout().catch(() => {
      // Continue with logout even if logging fails
    });
    setUser(null);
    setCurrentView('login');
    window.history.pushState({ view: 'login' }, '', '/login');
  };

  const handleAdminLoginSuccess = withLoading(async () => {
    // Small delay to show the loading transition and ensure token is stored
    await new Promise(resolve => setTimeout(resolve, 300));
    void import('./hooks/useLocale').then(({ ensureAdminEnglishLocale }) => {
      void ensureAdminEnglishLocale();
    });
    setCurrentView('admin-dashboard');
    window.history.pushState({}, '', '/admin/dashboard');
  }, t('loaders.loadingAdmin'));

  const notifyAdminLogoutStart = useCallback(() => {
    setAdminLogoutOverlay(true);
  }, []);

  const handleAdminLogout = useCallback(() => {
    adminService.clearAdminSession();
    setCurrentView('admin-login');
    window.history.pushState({}, '', '/admin/login');
    void import('./hooks/useLocale').then(({ ensureAdminEnglishLocale }) => {
      void ensureAdminEnglishLocale();
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setAdminLogoutOverlay(false));
    });
  }, []);

  const handleHomeClick = withLoading(async () => {
    void import('./hooks/useLocale').then(({ restoreLearnerLocaleFromStorage }) => {
      void restoreLearnerLocaleFromStorage();
    });
    setCurrentView('home');
    window.history.pushState({}, '', '/');
  }, t('loaders.goingHome'));

  const handleProfileClick = withLoading(async () => {
    if (homeDesign === 'mme') {
      window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view: 'profile', hash: 'menus' } }));
      return;
    }
    setProfileInitialTab('profile');
    setCurrentView('profile');
    window.history.pushState(
      { view: 'profile' },
      '',
      learnerPathForHistory() + window.location.search + '#profile'
    );
  }, t('loaders.loadingProfile'));

  const [selectedTagId, setSelectedTagId] = useState<number | undefined>(undefined);
  const [selectedTag, setSelectedTag] = useState<PublicContentTagDto | null>(null);
  const [selectedTagBlockId, setSelectedTagBlockId] = useState<number | undefined>(undefined);
  const [selectedTagLessonId, setSelectedTagLessonId] = useState<number | undefined>(undefined);
  const [selectedLessonId, setSelectedLessonId] = useState<number | undefined>(undefined);
  const [tagContentRefreshTrigger, setTagContentRefreshTrigger] = useState(0);
  const dashboardRefreshRef = useRef<(() => void) | null>(null);
  const prevViewRef = useRef<ViewType>(currentView);

  // Sync before paint so window event handlers (e.g. app:navigate from bottom banner) see the real view
  useLayoutEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  useLayoutEffect(() => {
    bookReturnViewRef.current = bookReturnView;
  }, [bookReturnView]);

  // Handle browser back/forward: restore view from history state so back from profile returns to previous page
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        if (path === '/admin/login') {
          setCurrentView('admin-login');
        } else if (adminService.isAdminAuthenticated()) {
          setCurrentView('admin-dashboard');
        } else {
          setCurrentView('admin-login');
        }
        return;
      }
      // Overlay modals push their own history entries; Back should dismiss them, not change view.
      if (consumeLearnerModalHistoryPop()) {
        return;
      }
      const state = event.state as LearnerHistoryState | null;
      if (
        state?.view &&
        [
          'dashboard',
          'profile',
          'lesson',
          'tag-content',
          'courses',
          'home',
          'quiz',
          'exercise-flow',
          'knowledge-placement',
          'book',
          'library',
          'static-page',
        ].includes(state.view)
      ) {
        const prevView = currentViewRef.current;
        const newView = state.view as ViewType;

        if (prevView === 'book' && newView !== 'book') {
          if (newView === 'dashboard') {
            setRefreshDashboardTrigger(Date.now());
            setDashboardKey((prev) => prev + 1);
          }
          clearBookSearchHighlight();
        }

        if ((prevView === 'lesson' || prevView === 'book') && newView === 'profile') {
          const tab = profileTabFromHash() ?? state.profileTab ?? profileReturnTabRef.current;
          if (tab) setProfileInitialTab(tab);
          profileReturnTabRef.current = null;
        } else if (prevView === 'lesson' || prevView === 'book') {
          profileReturnTabRef.current = null;
        }

        if (newView === 'book') {
          if (state.bookId != null && state.bookId > 0) {
            setSelectedBookId(state.bookId);
          }
          if (state.bookReturnView) {
            setBookReturnView(state.bookReturnView);
            bookReturnViewRef.current = state.bookReturnView;
          }
        }

        if (state.placementReturnView) {
          setPlacementReturnView(state.placementReturnView);
          placementReturnViewRef.current = state.placementReturnView;
        }

        if (newView === 'static-page') {
          if (state.staticPageKey) {
            setStaticPageKey(state.staticPageKey);
          }
          if (state.staticPageReturnView) {
            setStaticPageReturnView(state.staticPageReturnView);
            staticPageReturnViewRef.current = state.staticPageReturnView;
          }
        }

        setCurrentView(newView);
        if (newView === 'courses') {
          setCoursesHistoryRestore({
            epoch: Date.now(),
            inner: state.coursesInner ?? 'catalog',
            ...(state.courseId != null && state.courseId > 0 ? { courseId: state.courseId } : {}),
            ...(state.lessonId != null && state.lessonId > 0 ? { lessonId: state.lessonId } : {}),
          });
        }
        return;
      }
      if (path === '/login') {
        setCurrentView('login');
      } else if (path === '/register') {
        setCurrentView('register');
      } else if (user) {
        setCurrentView('dashboard');
      } else {
        setCurrentView('home');
      }
    };

    window.addEventListener('popstate', handlePopState);

    if (isOptimized) {
      preloadCriticalRoutes();
      if (adminService.isAdminAuthenticated()) {
        preloadAdminRoutes();
      }
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, [user, isOptimized, clearBookSearchHighlight]);

  // If the bar still shows /login or /register but we're already past those screens, normalize to / so history + hash stay correct (e.g. never /login#profile).
  useLayoutEffect(() => {
    if (!authService.isAuthenticated()) return;
    const p = window.location.pathname;
    if (p !== '/login' && p !== '/register') return;
    if (currentView === 'login' || currentView === 'register') return;
    window.history.replaceState(window.history.state ?? {}, '', '/' + window.location.search + window.location.hash);
  }, [currentView]);

  useEffect(() => {
    const prev = prevViewRef.current;
    if (prev === 'book' && currentView !== 'book') {
      clearBookSearchHighlight();
    }
    if (prev === 'lesson' && currentView !== 'lesson') {
      setLessonScrollToBlockId(undefined);
      setLessonOpenTipId(undefined);
      setLessonOpenExerciseId(undefined);
      setLessonOpenQuizFromSearchId(undefined);
      setLessonHighlightTerm(undefined);
    }
    const cameFromOtherView = currentView === 'dashboard' && prev !== 'dashboard';
    prevViewRef.current = currentView;
    if (cameFromOtherView) {
      const refreshTimer = setTimeout(() => dashboardRefreshRef.current?.(), 150);
      return () => clearTimeout(refreshTimer);
    }
    return undefined;
  }, [currentView, clearBookSearchHighlight]);

  const handleSectionChange = withLoading(async (section: string, tagId?: number, tag?: PublicContentTagDto) => {
    if (section === 'knowledge-placement') {
      openKnowledgePlacement(currentViewRef.current);
      return;
    }
    const view = section.startsWith('tag-content') ? ('tag-content' as ViewType) : (section as ViewType);
    if (section.startsWith('tag-content')) {
      setSelectedTagId(tagId);
      setSelectedTag(tag ?? null);
      setSelectedTagBlockId(undefined);
      setSelectedTagLessonId(undefined);
      setTagContentRefreshTrigger(prev => prev + 1);
      setCurrentView('tag-content' as ViewType);
    } else {
      setSelectedTagId(undefined);
      setSelectedTag(null);
      setSelectedTagBlockId(undefined);
      setSelectedTagLessonId(undefined);
      setCurrentView(section as ViewType);
      if (section === 'courses') {
        setCoursesHistoryRestore({ epoch: Date.now(), inner: 'catalog' });
      }
    }
    window.history.pushState(
      section === 'courses' || view === 'courses'
        ? { view: 'courses', coursesInner: 'catalog' }
        : { view },
      '',
      window.location.pathname || '/'
    );
  }, t('loaders.loadingSection'));

  const [keepLearningChoice, setKeepLearningChoice] = useState<{ open: boolean; entries: LearningResumeEntry[] }>({
    open: false,
    entries: []
  });
  const keepLearningModalRef = useRef<KeepLearningChoiceModalHandle>(null);

  const navigateToTagResume = useCallback(
    (tagId: number, blockId?: number, lessonId?: number) => {
      void consumeKnowledgePlacementDashboardIfNeeded();
      setSelectedLessonId(undefined);
      setSelectedTag(null);
      setSelectedTagId(tagId);
      if (lessonId != null) {
        setSelectedTagBlockId(undefined);
        setSelectedTagLessonId(lessonId);
      } else {
        setSelectedTagLessonId(undefined);
        setSelectedTagBlockId(blockId);
      }
      setTagContentRefreshTrigger((prev) => prev + 1);
      setCurrentView('tag-content');
      window.history.pushState({ view: 'tag-content' }, '', window.location.pathname || '/');
    },
    [consumeKnowledgePlacementDashboardIfNeeded]
  );

  const navigateResumeEntry = useCallback(
    (entry: LearningResumeEntry) => {
      setKeepLearningChoice((prev) => ({ ...prev, open: false }));
      void consumeKnowledgePlacementDashboardIfNeeded();

      switch (entry.payload.kind) {
        case 'lesson':
          setSelectedTagId(undefined);
          setSelectedTag(null);
          setSelectedTagBlockId(undefined);
          setSelectedTagLessonId(undefined);
          setSelectedLessonId(entry.payload.lessonId);
          setCurrentView('lesson');
          window.history.pushState({ view: 'lesson' }, '', window.location.pathname || '/');
          return;
        case 'tag': {
          const p = entry.payload;
          navigateToTagResume(p.tagId, p.blockId, p.lessonId);
          return;
        }
        case 'quiz':
          setSelectedLessonId(undefined);
          setSelectedTagId(undefined);
          setSelectedTag(null);
          setSelectedTagBlockId(undefined);
          setSelectedTagLessonId(undefined);
          setExerciseResumeHighlightItem(undefined);
          if (entry.payload.quizId != null && entry.payload.flowKind) {
            setQuizResumeHighlightItem({
              flowKind: entry.payload.flowKind,
              quizId: entry.payload.quizId
            });
          } else {
            setQuizResumeHighlightItem(undefined);
          }
          setCurrentView('quiz');
          window.history.pushState({ view: 'quiz' }, '', window.location.pathname || '/');
          return;
        case 'exercise':
          setSelectedLessonId(undefined);
          setSelectedTagId(undefined);
          setSelectedTag(null);
          setSelectedTagBlockId(undefined);
          setSelectedTagLessonId(undefined);
          setQuizResumeHighlightItem(undefined);
          if (entry.payload.exerciseId != null && entry.payload.flowKind) {
            setExerciseResumeHighlightItem({
              flowKind: entry.payload.flowKind,
              exerciseId: entry.payload.exerciseId
            });
          } else {
            setExerciseResumeHighlightItem(undefined);
          }
          setCurrentView('exercise-flow');
          window.history.pushState({ view: 'exercise-flow' }, '', window.location.pathname || '/');
          return;
        case 'courses':
          setSelectedTagId(undefined);
          setSelectedTag(null);
          setSelectedTagBlockId(undefined);
          setSelectedTagLessonId(undefined);
          setSelectedLessonId(undefined);
          setCoursesInitialCourseId(entry.payload.courseId);
          setCoursesInitialLevelId(entry.payload.levelId);
          setCurrentView('courses');
          window.history.pushState({ view: 'courses', coursesInner: 'catalog' }, '', window.location.pathname || '/');
          return;
        case 'book': {
          setSelectedTagId(undefined);
          setSelectedTag(null);
          setSelectedTagBlockId(undefined);
          setSelectedTagLessonId(undefined);
          setSelectedLessonId(undefined);
          clearBookSearchHighlight();
          const returnView = resolveBookReturnView(
            currentViewRef.current,
            bookReturnViewRef.current
          );
          setBookReturnView(returnView);
          bookReturnViewRef.current = returnView;
          setSelectedBookId(entry.payload.bookId);
          setBookInitialPageIndex(entry.payload.pageIndex);
          setCurrentView('book');
          window.history.pushState(
            {
              view: 'book',
              bookId: entry.payload.bookId,
              bookReturnView: returnView,
            } satisfies LearnerHistoryState,
            '',
            learnerPathForHistory()
          );
          return;
        }
        default:
          return;
      }
    },
    [consumeKnowledgePlacementDashboardIfNeeded, navigateToTagResume, clearBookSearchHighlight]
  );

  const {
    gateState: resumeNonFarsiGateState,
    gateWithoutClick: gateResumeNonFarsi,
    handleDismiss: handleResumeNonFarsiDismiss,
    handleContinue: handleResumeNonFarsiContinue,
    isRtl: resumeNonFarsiGateRtl,
    shouldGate: shouldGateResumeLesson,
  } = useNonFarsiContentGate();

  const onResumeNonFarsiDismiss = useCallback(() => {
    handleResumeNonFarsiDismiss();
    keepLearningModalRef.current?.resetSelection();
  }, [handleResumeNonFarsiDismiss]);

  const onResumeNonFarsiContinue = useCallback(
    (dontShowAgain: boolean) => {
      handleResumeNonFarsiContinue(dontShowAgain);
      keepLearningModalRef.current?.resetSelection();
    },
    [handleResumeNonFarsiContinue]
  );

  const handleResumeEntrySelect = useCallback(
    async (entry: LearningResumeEntry, anchorEl?: HTMLElement | null) => {
      let lessonId: number | undefined;
      let englishLocked: boolean | undefined;

      if (entry.payload.kind === 'lesson') {
        lessonId = entry.payload.lessonId;
        englishLocked = entry.payload.isDesignedForNonFarsiUsers;
      } else if (entry.payload.kind === 'tag' && entry.payload.lessonId != null) {
        lessonId = entry.payload.lessonId;
        englishLocked = entry.payload.lessonIsDesignedForNonFarsiUsers;
      }

      if (lessonId != null && englishLocked === undefined) {
        try {
          const detail = await contentService.getLessonDetail(lessonId);
          englishLocked = Boolean(detail.isDesignedForNonFarsiUsers);
        } catch {
          navigateResumeEntry(entry);
          keepLearningModalRef.current?.resetSelection();
          return;
        }
      }

      if (
        lessonId == null ||
        !shouldGateResumeLesson({ isDesignedForNonFarsiUsers: englishLocked }, 'lesson', lessonId)
      ) {
        navigateResumeEntry(entry);
        keepLearningModalRef.current?.resetSelection();
        return;
      }

      gateResumeNonFarsi(
        anchorEl ?? null,
        { isDesignedForNonFarsiUsers: true },
        'lesson',
        lessonId,
        () => {
          navigateResumeEntry(entry);
          keepLearningModalRef.current?.resetSelection();
        },
        { navigatesAway: true }
      );
    },
    [gateResumeNonFarsi, navigateResumeEntry, shouldGateResumeLesson]
  );

  const handleContinueLearning = useCallback(() => {
    const entries = getKeepLearningModalEntries();
    if (entries.length === 0) {
      void consumeKnowledgePlacementDashboardIfNeeded();
      setCurrentView('courses');
      window.history.pushState({ view: 'courses', coursesInner: 'catalog' }, '', window.location.pathname || '/');
      return;
    }
    if (entries.length === 1) {
      void handleResumeEntrySelect(entries[0]);
      return;
    }
    setKeepLearningChoice({ open: true, entries });
  }, [consumeKnowledgePlacementDashboardIfNeeded, handleResumeEntrySelect]);

  /** Admin login: no Persian loader — Suspense uses SimpleAdminOverlayLoader; avoids PWA full reload freeze. */
  const navigateToAdminLogin = useCallback(() => {
    void import('./hooks/useLocale').then(({ ensureAdminEnglishLocale }) => {
      void ensureAdminEnglishLocale();
    });
    setCurrentView('admin-login');
    window.history.pushState({}, '', '/admin/login');
  }, []);

  /** Learner sign-in from admin login screen — same admin-style Suspense fallback on login route. */
  const navigateToLearnerLogin = useCallback(() => {
    void import('./hooks/useLocale').then(({ restoreLearnerLocaleFromStorage }) => {
      void restoreLearnerLocaleFromStorage();
    });
    setCurrentView('login');
    window.history.pushState({ view: 'login' }, '', '/login');
  }, []);

  const goToLearnerDashboard = useCallback(() => {
    profileReturnTabRef.current = null;
    setSelectedTag(null);
    setSelectedTagId(undefined);
    setSelectedTagBlockId(undefined);
    setSelectedTagLessonId(undefined);
    setSelectedLessonId(undefined);
    setCoursesHistoryRestore(null);
    setRefreshDashboardTrigger(Date.now());
    setDashboardKey((prev) => prev + 1);
    setCurrentView('dashboard');
    window.history.pushState({ view: 'dashboard' }, '', learnerPathForHistory());
  }, []);

  const handleLessonViewerBack = useCallback(() => {
    const tab = profileReturnTabRef.current;
    if (tab) {
      setProfileInitialTab(tab);
      window.history.back();
      return;
    }
    goToLearnerDashboard();
  }, [goToLearnerDashboard]);

  const handleViewChange = (view: ViewType) => {
    if (view === 'admin-login') {
      navigateToAdminLogin();
      return;
    }

    const message = view === 'login' ? t('loaders.goingLogin') :
      view === 'register' ? t('loaders.goingRegister') :
          t('loading');

    return withLoading(async () => {
      if (view === 'dashboard') {
        goToLearnerDashboard();
        return;
      }

      setCurrentView(view);

      if (view === 'home') {
        window.history.pushState({ view: 'home' }, '', '/');
      } else if (view === 'login') {
        window.history.pushState({ view: 'login' }, '', '/login');
      } else if (view === 'register') {
        window.history.pushState({ view: 'register' }, '', '/register');
      }
    }, message)();
  };

  /** From achievement notification: scroll/highlight only — no full-screen loader or dashboard remount. */
  const goToMyAchievements = useCallback((achievementId: number) => {
    setDashboardHighlightAchievementId(achievementId);
    setDashboardScrollToSectionId('my-achievements');
    if (currentView !== 'dashboard') {
      setCurrentView('dashboard');
      window.history.pushState({ view: 'dashboard' }, '', window.location.pathname || window.location.search || '/');
    }
  }, [currentView]);

  const onScrollToSectionConsumed = useCallback(() => {
    setDashboardScrollToSectionId(null);
  }, []);

  const onHighlightAchievementConsumed = useCallback(() => {
    setDashboardHighlightAchievementId(null);
  }, []);

  const renderView = () => {
    const showNavigation = !currentView.startsWith('admin');
    const adminFallback = (message: string) => (
      <SimpleAdminOverlayLoader isVisible message={message} />
    );

    const content = (() => {
      switch (currentView) {
        case 'login':
          return (
            <LazyRouteWrapper fallback={adminFallback(t('loaders.loadingSignIn'))}>
              <LazyLogin
                onLoginSuccess={handleLoginSuccess}
                onLoginError={(error: any) => {
                  console.log('App: Login error received, staying on login page:', error);
                  // Don't change view - stay on login page
                }}
                onSwitchToRegister={() => handleViewChange('register')}
                onAdminLoginClick={navigateToAdminLogin}
              />
            </LazyRouteWrapper>
          );
        case 'register':
          return (
            <LazyRouteWrapper>
              <LazyRegister
                onRegisterSuccess={handleLoginSuccess}
                onSwitchToLogin={() => handleViewChange('login')}
              />
            </LazyRouteWrapper>
          );
        case 'dashboard':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <LazyDashboard
                  key={`dashboard-${dashboardKey}-${refreshDashboardTrigger}`}
                  user={user}
                  refreshTrigger={refreshDashboardTrigger}
                  onRegisterRefresh={(fn: (() => void) | null) => { dashboardRefreshRef.current = fn ?? null; }}
                  onLogout={handleLogout}
                  onSectionChange={handleSectionChange}
                  onContinueLearning={handleContinueLearning}
                  onNavigateToLesson={navigateToLessonAndConsumeKp}
                  onOpenBook={(bookId) => {
                    const id = normalizeLibraryBookId(bookId);
                    if (id != null) navigateToBook(id);
                  }}
                  onOpenLibrary={navigateToLibrary}
                  scrollToSectionId={dashboardScrollToSectionId}
                  onScrollToSectionConsumed={onScrollToSectionConsumed}
                  highlightAchievementId={dashboardHighlightAchievementId}
                  onHighlightAchievementConsumed={onHighlightAchievementConsumed}
                  onOpenStaticPage={openStaticPage}
                />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'admin-login':
          return (
            <LazyRouteWrapper fallback={adminFallback(t('loaders.loadingSignIn'))}>
              <LazyAdminLogin
                onLoginSuccess={handleAdminLoginSuccess}
                onBackToLearnerLogin={navigateToLearnerLogin}
              />
            </LazyRouteWrapper>
          );
        case 'admin-dashboard':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper fallback={adminFallback(t('loaders.loadingAdmin'))}>
                <LazyAdminRouter
                  onLogout={handleAdminLogout}
                  onAdminLogoutStart={notifyAdminLogoutStart}
                />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'grammar':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <LazyGrammar onBackToDashboard={goToLearnerDashboard} />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'quiz':
          return (
            <ErrorBoundary>
              <QuizFlow
                onBack={goToLearnerDashboard}
                onBackToDashboard={goToLearnerDashboard}
                onNavigateToLesson={(lessonId) => {
                  navigateToLessonAndConsumeKp(lessonId);
                }}
                {...(quizResumeHighlightItem ? { highlightItem: quizResumeHighlightItem } : {})}
                {...(quizResumeHighlightItem ? { onHighlightConsumed: clearQuizResumeHighlight } : {})}
              />
            </ErrorBoundary>
          );
        case 'knowledge-placement':
          return user?.id ? (
            <ErrorBoundary>
              <KnowledgePlacementWizard
                userId={user.id}
                onBack={handleKnowledgePlacementBack}
                onBackToDashboard={goToLearnerDashboard}
                onNavigateToLesson={(lessonId) => {
                  navigateToLessonAndConsumeKp(lessonId);
                }}
              />
            </ErrorBoundary>
          ) : null;
        case 'exercise-flow':
          return (
            <ErrorBoundary>
              <ExerciseFlow
                onBack={goToLearnerDashboard}
                onBackToDashboard={goToLearnerDashboard}
                onNavigateToLesson={(lessonId) => {
                  navigateToLessonAndConsumeKp(lessonId);
                }}
                {...(exerciseResumeHighlightItem ? { highlightItem: exerciseResumeHighlightItem } : {})}
                {...(exerciseResumeHighlightItem ? { onHighlightConsumed: clearExerciseResumeHighlight } : {})}
              />
            </ErrorBoundary>
          );
        case 'pronunciation':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <LazyPronunciation onBackToDashboard={goToLearnerDashboard} />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'courses':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <LazyCoursesSection
                  onBackToDashboard={goToLearnerDashboard}
                  initialCourseId={coursesInitialCourseId}
                  onInitialCourseConsumed={clearCoursesInitialCourse}
                  initialLevelId={coursesInitialLevelId}
                  onInitialLevelConsumed={clearCoursesInitialLevel}
                  historyRestore={coursesHistoryRestore}
                  {...(coursesSearchHighlightCourseId != null && coursesSearchHighlightCourseId > 0
                    ? { highlightCourseId: coursesSearchHighlightCourseId }
                    : {})}
                  {...(coursesSearchHighlightTerm ? { highlightTerm: coursesSearchHighlightTerm } : {})}
                  {...(coursesSearchHighlightCourseId != null && coursesSearchHighlightCourseId > 0
                    ? { onHighlightCourseConsumed: clearCoursesSearchCatalogHighlight }
                    : {})}
                  {...(coursesSearchDetailHighlightItem ? { detailHighlightItem: coursesSearchDetailHighlightItem } : {})}
                  {...(coursesSearchDetailHighlightTerm
                    ? { detailHighlightTerm: coursesSearchDetailHighlightTerm }
                    : {})}
                  {...(coursesSearchDetailHighlightItem ? { onDetailHighlightConsumed: clearCoursesSearchDetailHighlight } : {})}
                  onLearningSearchScopeChange={setLearningSearchScope}
                />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'tag-content':
          return (
            <ErrorBoundary>
              {selectedTagId ? (
                <TagContentView
                  key={`tag-${selectedTagId}-${selectedTagLessonId ?? 'n'}-${selectedTagBlockId ?? 'n'}-${tagContentRefreshTrigger}`}
                  tagId={selectedTagId}
                  {...(selectedTag && selectedTag.id === selectedTagId ? { tag: selectedTag } : {})}
                  {...(selectedTagBlockId !== undefined ? { initialBlockId: selectedTagBlockId } : {})}
                  {...(selectedTagLessonId !== undefined ? { initialLessonId: selectedTagLessonId } : {})}
                  refreshTrigger={tagContentRefreshTrigger}
                  onBack={goToLearnerDashboard}
                  onNavigateToLesson={(lessonId) => {
                    navigateToLessonAndConsumeKp(lessonId);
                  }}
                />
              ) : (
                <div className="p-6 text-center">
                  <p className="text-gray-600">Tag not found</p>
                  <button
                    onClick={() => {
                      setRefreshDashboardTrigger(Date.now());
                      setDashboardKey(prev => prev + 1);
                      setCurrentView('dashboard');
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
                  >
                    Go Back
                  </button>
                </div>
              )}
            </ErrorBoundary>
          );
        case 'library':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <LazyLibraryPage
                  onOpenBook={(id: number) => navigateToBook(id, 'library')}
                  onBack={goToLearnerDashboard}
                />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'book':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                {selectedBookId ? (
                  <LazyBookViewer
                    bookId={selectedBookId}
                    {...(bookInitialPageIndex != null ? { initialPageIndex: bookInitialPageIndex } : {})}
                    onInitialPageIndexConsumed={() => setBookInitialPageIndex(undefined)}
                    {...(bookScrollToBlockId != null ? { scrollToBlockId: bookScrollToBlockId } : {})}
                    {...(bookHighlightTerm ? { highlightTerm: bookHighlightTerm } : {})}
                    onScrollToBlockConsumed={() => {
                      /* Keep scrollToBlockId until highlight ends — clearing early made BookViewer drop block target and pulse the header. */
                    }}
                    onHighlightTermConsumed={() => {
                      setBookHighlightTerm(undefined);
                      setBookScrollToBlockId(undefined);
                    }}
                    onBack={handleBookViewerBack}
                    onBackToDashboard={goToLearnerDashboard}
                  />
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-gray-600">Book not found</p>
                    <button
                      type="button"
                      onClick={() => setCurrentView('dashboard')}
                      className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
                    >
                      Go Back
                    </button>
                  </div>
                )}
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'lesson':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                {selectedLessonId ? (
                  <LazyLessonViewer
                    lessonId={selectedLessonId}
                    {...(lessonScrollToBlockId != null ? { scrollToContentBlockId: lessonScrollToBlockId } : {})}
                    {...(lessonOpenTipId ? { openTipId: lessonOpenTipId } : {})}
                    {...(lessonOpenExerciseId != null && lessonOpenExerciseId > 0
                      ? { openExerciseId: lessonOpenExerciseId }
                      : {})}
                    {...(lessonOpenQuizFromSearchId != null && lessonOpenQuizFromSearchId > 0
                      ? { openLessonQuizFromSearchId: lessonOpenQuizFromSearchId }
                      : {})}
                    {...(lessonHighlightTerm ? { highlightTerm: lessonHighlightTerm } : {})}
                    onScrollToBlockConsumed={() => setLessonScrollToBlockId(undefined)}
                    onOpenTipConsumed={() => setLessonOpenTipId(undefined)}
                    onOpenExerciseConsumed={() => setLessonOpenExerciseId(undefined)}
                    onOpenLessonQuizFromSearchConsumed={() => setLessonOpenQuizFromSearchId(undefined)}
                    onHighlightTermConsumed={() => setLessonHighlightTerm(undefined)}
                    onLearningSearchScopeChange={setLearningSearchScope}
                    onBack={handleLessonViewerBack}
                    onBackToDashboard={goToLearnerDashboard}
                    onNavigateToLesson={(lessonId: number) => {
                      setLessonScrollToBlockId(undefined);
                      setLessonOpenTipId(undefined);
                      setLessonOpenExerciseId(undefined);
                      setLessonOpenQuizFromSearchId(undefined);
                      setLessonHighlightTerm(undefined);
                      setSelectedLessonId(lessonId);
                      // Stay on lesson view, just update the lesson ID
                    }}
                  />
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-gray-600">Lesson not found</p>
                    <button
                      onClick={() => {
                        setRefreshDashboardTrigger(Date.now());
                        setDashboardKey(prev => prev + 1);
                        setCurrentView('dashboard');
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
                    >
                      Go Back
                    </button>
                  </div>
                )}
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        case 'static-page':
          return (
            <ErrorBoundary>
              <StaticPageView
                pageKey={staticPageKey}
                onBack={handleStaticPageBack}
                onPageKeyChange={navigateStaticPageTab}
              />
            </ErrorBoundary>
          );
        case 'profile':
          return (
            <ErrorBoundary>
              <LazyRouteWrapper>
                <UserProfileDashboard
                  initialUser={user}
                  initialTab={profileInitialTab}
                  onInitialTabConsumed={() => setProfileInitialTab(null)}
                  onNavigateHome={goToLearnerDashboard}
                  onNavigateToLesson={(lessonId) => {
                    navigateToLessonFromProfile(lessonId);
                  }}
                  onNavigateToTag={(tagId, blockId, lessonId) => {
                    navigateToTagResume(tagId, blockId, lessonId);
                  }}
                  onNavigateToBook={(bookId) => {
                    stampProfileReturnEntry(profileTabFromHash() ?? 'profile');
                    navigateToBook(bookId, 'profile');
                  }}
                  onUserUpdate={(updatedUser) => {
                    console.log('App: Received user update:', updatedUser);
                    console.log('App: Updated user has photo:', updatedUser?.profilePhoto ? 'YES' : 'NO');
                    
                    // Force a complete re-render by creating a new user object
                    const newUser = { ...updatedUser, _updateTimestamp: Date.now() };
                    setUser(newUser);
                    
                    // Also update localStorage to persist the changes
                    localStorage.setItem('userProfile', JSON.stringify(updatedUser));
                    console.log('App: Updated localStorage with new user data');
                    
                    // Force a small delay to ensure state propagation
                    setTimeout(() => {
                      console.log('App: User state should be updated now');
                    }, 100);
                  }}
                  onOpenKnowledgePlacement={() => openKnowledgePlacement('profile')}
                />
              </LazyRouteWrapper>
            </ErrorBoundary>
          );
        default:
          return (
            <ErrorBoundary>
              <HomePage
                onLoginClick={() => handleViewChange('login')}
                onRegisterClick={() => handleViewChange('register')}
                onAdminLoginClick={navigateToAdminLogin}
                onDashboardClick={() => handleViewChange('dashboard')}
                user={user}
                onLogout={handleLogout}
                onOpenStaticPage={openStaticPage}
              />
            </ErrorBoundary>
          );
      }
    })();

    return (
      <>
        {showNavigation && (
          <Navigation
            user={user}
            currentView={currentView}
            onHomeClick={handleHomeClick}
            onLogout={handleLogout}
            onProfileClick={handleProfileClick}
            onLoginClick={() => handleViewChange('login')}
            sideNavOpen={sideNavOpen}
            onSideNavToggle={() => setSideNavOpen((open) => !open)}
            sideNavPanelId={sideNavPanelId}
          />
        )}
        <div
          className={`min-h-screen w-full ${showNavigation ? 'fixed-nav-padding' : ''} ${
            user && !currentView.startsWith('admin') ? 'bottom-banner-layout-reserve' : ''
          }`}
          style={{
            ...(showNavigation ? { paddingTop: '4rem' } : {}),
            ...(user && !currentView.startsWith('admin')
              ? {
                  paddingBottom: audioBarPinned
                    ? (bannerCollapsed ? '5.5rem' : '10rem')
                    : (bannerCollapsed ? '1rem' : '4.5rem')
                }
              : {})
          }}
        >
          {content}
        </div>
      </>
    );
  };

  return (
    <LoadingStateManager>
      <LocaleProvider>
      <ThemeModeToast />
      <NotificationSystem position="top-center">
        <ExternalOAuthHandler onLoginSuccess={handleLoginSuccess} />
        <GlobalErrorHandler>
          <NetworkStatusManager>
            <ApiProvider>
              <AuthProvider>
                <div className="relative min-h-screen w-full overflow-x-clip">
                  <FerdowsiBackground />

                  {!currentView.startsWith('admin') ? (
                    <ScoreNotifProvider
                      learnerView={currentView}
                      onGoToMyAchievements={user ? goToMyAchievements : undefined}
                    >
                      {user ? (
                        <VisibleBlockTrackerProvider>
                          <VisibleBlockTrackerResetter currentView={currentView} />
                          <FontSizeProvider>
                            <ProductTourProvider
                              initialPageKey={learnerTourPageKey}
                              enabled={Boolean(user)}
                            >
                            <StickyAudioProvider>
                              {measureComponentRender('App', renderView)}
                              <LearnerSideNav
                                user={user}
                                open={sideNavOpen}
                                onOpenChange={setSideNavOpen}
                                panelId={sideNavPanelId}
                                onHomeClick={handleHomeClick}
                                onDashboardClick={() => handleViewChange('dashboard')}
                                onOpenLibrary={navigateToLibrary}
                                onOpenCourses={() => handleViewChange('courses')}
                                onOpenStaticPage={openStaticPage}
                                onNavigateProfile={(hash) => {
                                  window.dispatchEvent(
                                    new CustomEvent('app:navigate', { detail: { view: 'profile', hash } })
                                  );
                                }}
                              />
                              <BottomBanner
                                onCollapsedChange={setBannerCollapsed}
                                onDashboardClick={() => handleViewChange('dashboard')}
                                onOpenSettings={() => {
                                  setLearnerSettingsInitialTab(undefined);
                                  setLearnerSettingsFocusInAppAlerts(false);
                                  setLearnerSettingsOpen(true);
                                }}
                                onHomeClick={handleHomeClick}
                                onKeepLearning={handleContinueLearning}
                                canLimitSearchToLearningContext={canLimitSearchToLearningContext}
                                learningSearchScope={learningSearchScope}
                              />
                              <NonFarsiContentHintModal
                                open={resumeNonFarsiGateState.open}
                                isRtl={resumeNonFarsiGateRtl}
                                anchorRect={resumeNonFarsiGateState.anchorRect}
                                contentKey={resumeNonFarsiGateState.contentKey}
                                onDismiss={onResumeNonFarsiDismiss}
                                onContinue={onResumeNonFarsiContinue}
                              />
                              <LearnerSettingsModal
                                open={learnerSettingsOpen}
                                onClose={() => {
                                  setLearnerSettingsOpen(false);
                                  setLearnerSettingsInitialTab(undefined);
                                  setLearnerSettingsFocusInAppAlerts(false);
                                }}
                                onOpenProfileGoals={() => {
                                  window.dispatchEvent(new CustomEvent(LEARNER_SETTINGS_MODAL_CLOSED_EVENT));
                                  setLearnerSettingsOpen(false);
                                  setLearnerSettingsInitialTab(undefined);
                                  setLearnerSettingsFocusInAppAlerts(false);
                                  setProfileInitialTab('goals');
                                  setCurrentView('profile');
                                  // Replace the settings history entry so Back from goals leaves the modal stack cleanly.
                                  window.history.replaceState(
                                    { view: 'profile' },
                                    '',
                                    learnerPathForHistory() + window.location.search + '#goals'
                                  );
                                }}
                                {...(learnerSettingsInitialTab ? { initialTab: learnerSettingsInitialTab } : {})}
                                {...(learnerSettingsFocusInAppAlerts ? { focusInAppAlerts: true } : {})}
                                {...(learnerSettingsFocusRequestId ? { focusRequestId: learnerSettingsFocusRequestId } : {})}
                              />
                            </StickyAudioProvider>
                            </ProductTourProvider>
                          </FontSizeProvider>
                        </VisibleBlockTrackerProvider>
                      ) : (
                        measureComponentRender('App', renderView)
                      )}
                    </ScoreNotifProvider>
                  ) : (
                    measureComponentRender('App', renderView)
                  )}

                  {!currentView.startsWith('admin') && <PwaUpdateBanner />}
                  {!currentView.startsWith('admin') && <PwaInstallBanner />}
                  {!currentView.startsWith('admin') && <PwaPushPermissionBanner user={user} />}

                  {!currentView.startsWith('admin') && user && (
                    <LearnerEdgeActions
                      isMobile={isMobile}
                      audioBarPinned={audioBarPinned}
                      audioBarExtraChromePx={audioBarExtraChromePx}
                      bannerCollapsed={bannerCollapsed}
                      keepLearningOpen={keepLearningChoice.open}
                      keepLearningEntries={keepLearningChoice.entries}
                      keepLearningModalRef={keepLearningModalRef}
                      onKeepLearningClose={() => setKeepLearningChoice((prev) => ({ ...prev, open: false }))}
                      onKeepLearningAfterClose={() => setKeepLearningChoice({ open: false, entries: [] })}
                      onKeepLearningSelect={(entry, anchorEl) => {
                        void handleResumeEntrySelect(entry, anchorEl);
                      }}
                    />
                  )}
                  
                  {/* Use different loaders for admin vs user routes */}
                  {currentView.startsWith('admin') ? (
                    <SimpleAdminOverlayLoader
                      isVisible={isAppLoading}
                      message={loadingMessage || t('loading')}
                    />
                  ) : (
                    <PersianOverlayLoader
                      isVisible={isAppLoading}
                      message={loadingMessage || t('loading')}
                    />
                  )}

                  <SimpleAdminOverlayLoader
                    stackClassName="z-[100]"
                    isVisible={adminLogoutOverlay}
                    message={t('loggingOut')}
                  />
                </div>
              </AuthProvider>
            </ApiProvider>
          </NetworkStatusManager>
        </GlobalErrorHandler>
      </NotificationSystem>
      </LocaleProvider>
    </LoadingStateManager>
  );
}

export default App;